import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckSquare, Mail, MessageSquare, Phone, Plus, Settings2, Trash2, X } from 'lucide-react'
import {
  useContact,
  useContacts,
  useContactSearch,
  useContactDeals,
  useContactDuplicates,
  useUpdateContact,
  useMergeContact,
  useFieldDefinitions,
} from '@/features/contacts/useContacts'
import {
  useAddContactActivity,
  useContactActivities,
  useDeleteContactActivity,
  usePinContactActivity,
  useUpdateContactActivity,
} from '@/features/activity/useActivities'
import { useDeals, useLinkDealToContact } from '@/features/board/useBoard'
import { useDealContactRoles } from '@/features/agency/useDealContactRoles'
import { useAgencyQuery } from '@/features/agency/useAgency'
import { useMembers } from '@/features/team/useTeam'
import { useAuthStore } from '@/features/auth/authStore'
import { useSendContactMessage } from '@/features/messaging/useMessaging'
import { ApiError } from '@/api/client'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { MESSAGE_TOKENS } from '@/features/messaging/tokens'
import Select from '@/components/ui/Select'
import SearchableSelect from '@/components/ui/SearchableSelect'
import OwnerCard from '@/components/OwnerCard'
import CustomFieldGroup from '@/components/CustomFieldGroup'
import ActionMenu from '@/components/ui/ActionMenu'
import { DeleteContactModal } from '@/features/contacts/ContactRowModals'
import NewDealModal from '@/features/deals/NewDealModal'
import ContactTasksCard from '@/features/tasks/ContactTasksCard'
import AttachmentsCard from '@/features/attachments/AttachmentsCard'
import MessageComposer from '@/features/messaging/MessageComposer'
import { useComposerDraft, type ComposerDraft } from '@/features/messaging/useComposerDraft'
import ActivityLog from '@/components/ActivityLog'
import SaveState from '@/components/SaveState'
import DetailDrawer from '@/components/layout/DetailDrawer'
import Card from '@/components/layout/Card'
import FieldsTab from '@/features/settings/FieldsTab'
import { useAllFieldDefinitions } from '@/features/settings/useFieldsAdmin'
import { validateEmail, validatePhone } from '@/lib/contactValidation'
import { formatShortDate, relativeDay } from '@/lib/dealFormat'
import { cn } from '@/lib/cn'
import { useAutosave } from '@/lib/useAutosave'
import type { Contact } from '@/api/types'
import type { UpdateContactParams } from '@/api/contacts'
import type { Activity } from '@/api/activities'

const contactName = (c: Contact) =>
  [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Contact'

const fieldCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/** A built-in contact field: renders its own input for the (possibly relabelled) label. */
interface CoreFieldDef {
  key: string
  defaultLabel: string
  hideable: boolean
  render: (label: string) => ReactNode
}

interface LayoutEntry {
  key: string
  label?: string
  hidden?: boolean
}

/**
 * Merge the agency layout over the default core-field list: listed keys first (in that order), then any
 * remaining fields in their default order; apply label overrides; drop hidden ones (essentials never hide).
 */
function resolveCoreLayout(defs: CoreFieldDef[], layout: LayoutEntry[]) {
  const byKey = new Map(layout.map((l) => [l.key, l]))
  const orderedKeys = [
    ...layout.map((l) => l.key).filter((k) => defs.some((d) => d.key === k)),
    ...defs.map((d) => d.key).filter((k) => !byKey.has(k)),
  ]
  return orderedKeys
    .map((k) => {
      const def = defs.find((d) => d.key === k) as CoreFieldDef
      const entry = byKey.get(k)
      const hidden = def.hideable ? Boolean(entry?.hidden) : false
      const label = entry?.label?.trim() || def.defaultLabel
      return { key: k, label, hidden, render: def.render }
    })
    .filter((r) => !r.hidden)
}

export default function ContactDetail({
  contactId,
  onClose,
  defaultExpanded,
}: {
  contactId: string
  onClose: () => void
  /** Open already full-screen (arrived via the record's own /contacts/:id URL). */
  defaultExpanded?: boolean
}) {
  const { data: contact, isError } = useContact(contactId)

  return (
    <DetailDrawer
      onClose={onClose}
      defaultExpanded={defaultExpanded}
      width="w-[34rem] max-w-[95vw]"
      openInNewTabHref={`/contacts/${contactId}`}
      title={
        contact ? (
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-brand-darkest truncate">{contactName(contact)}</h2>
            <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5 truncate">
              <span
                className={cn('inline-block w-2 h-2 rounded-full shrink-0', contact.doNotEmail ? 'bg-gray-300' : 'bg-success')}
              />
              {[contact.email, contact.mobile || contact.phone].filter(Boolean).join(' · ') || 'No contact details'}
            </p>
          </div>
        ) : (
          <h2 className="text-sm font-semibold text-brand-darkest">Contact</h2>
        )
      }
    >
      {(expanded) =>
        !contact ? (
          <div className="flex-1 p-4">
            {isError ? (
              <p className="text-sm text-gray-600">
                This contact can’t be opened — they may have been deleted, or the link is incomplete.{' '}
                <a href="/contacts" className="text-brand-primary hover:underline">Go to Contacts</a>
              </p>
            ) : (
              <p className="text-gray-400 text-sm">Loading…</p>
            )}
          </div>
        ) : (
          // keyed so the form state re-initializes when another contact is opened
          <ContactBody key={contact.id} contact={contact} onClose={onClose} expanded={expanded} />
        )
      }
    </DetailDrawer>
  )
}

/** Mounted only once the contact is loaded, so state initializes from it without an effect. */
function ContactBody({
  contact,
  onClose,
  expanded,
}: {
  contact: Contact
  onClose: () => void
  expanded: boolean
}) {
  const contactId = contact.id
  const { data: fields } = useFieldDefinitions('contact')
  useAllFieldDefinitions() // warm the "Manage fields" data so the overlay opens instantly, not empty
  const { data: activities } = useContactActivities(contactId)
  const update = useUpdateContact()
  const subscribe = useUpdateContact() // immediate save for the email subscription toggle (own pending state)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [addingTask, setAddingTask] = useState(false)
  const addActivity = useAddContactActivity(contactId)
  const updateActivity = useUpdateContactActivity(contactId)
  const removeActivity = useDeleteContactActivity(contactId)
  const pinActivity = usePinContactActivity(contactId)

  const { data: members } = useMembers() // roster available to any authenticated user
  const role = useAuthStore((s) => s.user?.role)
  // Admin-only operations: reassigning the owner and managing custom-field definitions.
  const isAdmin = role === 'AGENCY_ADMIN' || role === 'PLATFORM_ADMIN'
  const { data: agency } = useAgencyQuery()

  const [firstName, setFirstName] = useState(contact.firstName ?? '')
  const [lastName, setLastName] = useState(contact.lastName ?? '')
  const [email, setEmail] = useState(contact.email ?? '')
  const [phone, setPhone] = useState(contact.phone ?? '')
  const [mobile, setMobile] = useState(contact.mobile ?? '')
  const [ownerUserId, setOwnerUserId] = useState(contact.ownerUserId ?? '')
  const [leadSource, setLeadSource] = useState(contact.leadSource ?? '')
  const [birthday, setBirthday] = useState(contact.birthday ?? '')
  // doNotEmail is NOT held in form state: the subscription block shows the live server value and saves
  // on toggle, so it reflects out-of-band changes (contact (un)subscribing via the email link).
  const [doNotCall, setDoNotCall] = useState(contact.doNotCall)
  const [doNotSms, setDoNotSms] = useState(contact.doNotSms)
  const [nextContactDate, setNextContactDate] = useState(contact.nextContactDate ?? '')
  const [custom, setCustom] = useState<Record<string, unknown>>(contact.custom ?? {})
  // "Manage fields" opens in an overlay above the contact instead of navigating away
  // (router navigation would unmount the surface and lose the open contact).
  const [showFields, setShowFields] = useState(false)
  // Draft persists per-contact (sessionStorage); held here so the header's SMS/Email buttons can
  // preset the channel and jump to the composer instead of handing off to a native sms:/mailto: app.
  const { draft, update: updateDraft } = useComposerDraft(`contact:${contactId}`)
  const composerRef = useRef<HTMLDivElement>(null)

  function composeIn(channel: 'sms' | 'email') {
    updateDraft({ channel })
    composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const emailError = validateEmail(email)
  const phoneError = validatePhone(phone)
  const mobileError = validatePhone(mobile)
  const hasErrors = !!(emailError || phoneError || mobileError)

  // Resync the edit state whenever the server copy changes (chiefly after a save), mirroring the deal
  // panel. Saving no longer closes the card — the user stays where they were and keeps working.
  const [syncedAt, setSyncedAt] = useState(contact.updatedAt)
  if (contact.updatedAt !== syncedAt) {
    setSyncedAt(contact.updatedAt)
    setFirstName(contact.firstName ?? '')
    setLastName(contact.lastName ?? '')
    setEmail(contact.email ?? '')
    setPhone(contact.phone ?? '')
    setMobile(contact.mobile ?? '')
    setOwnerUserId(contact.ownerUserId ?? '')
    setLeadSource(contact.leadSource ?? '')
    setBirthday(contact.birthday ?? '')
    setDoNotCall(contact.doNotCall)
    setDoNotSms(contact.doNotSms)
    setNextContactDate(contact.nextContactDate ?? '')
    setCustom(contact.custom ?? {})
  }

  function setField(key: string, v: unknown) {
    setCustom((c) => ({ ...c, [key]: v }))
  }

  /** The whole contact as the API wants it — shared by save, autosave and undo. */
  function paramsFrom(v: {
    firstName: string
    lastName: string
    email: string | null
    phone: string | null
    mobile: string | null
    ownerUserId: string
    leadSource: string
    birthday: string
    nextContactDate: string
    doNotCall: boolean
    doNotSms: boolean
    custom: Record<string, unknown>
  }): UpdateContactParams {
    return {
      firstName: v.firstName || null,
      lastName: v.lastName || null,
      email: v.email || null,
      phone: v.phone || null,
      mobile: v.mobile || null,
      ownerUserId: v.ownerUserId || null,
      leadSource: v.leadSource || null,
      status: contact.status ?? null,
      birthday: v.birthday || null,
      nextContactDate: v.nextContactDate || null,
      doNotEmail: contact.doNotEmail, // owned by the subscription toggle, not the form
      doNotCall: v.doNotCall,
      doNotSms: v.doNotSms,
      custom: v.custom,
    }
  }

  const currentValues = {
    firstName,
    lastName,
    email,
    phone,
    mobile,
    ownerUserId,
    leadSource,
    birthday,
    nextContactDate,
    doNotCall,
    doNotSms,
    custom,
  }

  // How someone is REACHED is confirmed, never saved behind the user's back: getting a phone number
  // or an email address wrong costs a deal, which is why the client's CEO carved these out of
  // autosave by name. Everything else about the contact saves itself.
  const contactMethodsDirty =
    email !== (contact.email ?? '') || phone !== (contact.phone ?? '') || mobile !== (contact.mobile ?? '')
  const detailsDirty =
    firstName !== (contact.firstName ?? '') ||
    lastName !== (contact.lastName ?? '') ||
    ownerUserId !== (contact.ownerUserId ?? '') ||
    leadSource !== (contact.leadSource ?? '') ||
    birthday !== (contact.birthday ?? '') ||
    nextContactDate !== (contact.nextContactDate ?? '') ||
    doNotCall !== contact.doNotCall ||
    doNotSms !== contact.doNotSms ||
    JSON.stringify(custom) !== JSON.stringify(contact.custom ?? {})

  function persist(params: UpdateContactParams, done?: (ok: boolean) => void) {
    update.mutate(
      { id: contactId, params },
      {
        // Adopt the saved version without replaying it into the fields: a request is often in flight
        // while the user keeps typing (and an unconfirmed phone/email edit must survive an autosave
        // of the fields around it), and the resync above would discard both.
        onSuccess: (saved) => {
          setSyncedAt(saved.updatedAt)
          done?.(true)
        },
        onError: () => done?.(false),
      },
    )
  }

  function save(done?: (ok: boolean) => void) {
    if (hasErrors) {
      done?.(false)
      return
    }
    persist(paramsFrom(currentValues), done)
  }

  const autosave = useAutosave({
    // Only the fields that save themselves re-arm the pause — typing a phone number must not trigger
    // a save of anything.
    values: {
      firstName,
      lastName,
      ownerUserId,
      leadSource,
      birthday,
      nextContactDate,
      doNotCall,
      doNotSms,
      custom,
    },
    dirty: detailsDirty,
    // Not blocked by a phone/email validation error: autosave never sends those fields, so a
    // half-typed number must not stop the name beside it from saving.
    capture: () =>
      paramsFrom({
        firstName: contact.firstName ?? '',
        lastName: contact.lastName ?? '',
        ownerUserId: contact.ownerUserId ?? '',
        leadSource: contact.leadSource ?? '',
        birthday: contact.birthday ?? '',
        nextContactDate: contact.nextContactDate ?? '',
        doNotCall: contact.doNotCall,
        doNotSms: contact.doNotSms,
        custom: contact.custom ?? {},
        ...savedContactMethods(),
      }),
    // Send the SAVED phone/email, not what is being typed into them: an in-progress correction stays
    // unconfirmed until the user presses Save.
    save: (done) => persist(paramsFrom({ ...currentValues, ...savedContactMethods() }), done),
    restore: (params, done) => {
      setFirstName(params.firstName ?? '')
      setLastName(params.lastName ?? '')
      setOwnerUserId(params.ownerUserId ?? '')
      setLeadSource(params.leadSource ?? '')
      setBirthday(params.birthday ?? '')
      setNextContactDate(params.nextContactDate ?? '')
      setDoNotCall(!!params.doNotCall)
      setDoNotSms(!!params.doNotSms)
      setCustom((params.custom ?? {}) as Record<string, unknown>)
      persist(params, done)
    },
  })

  function savedContactMethods() {
    return { email: contact.email, phone: contact.phone, mobile: contact.mobile }
  }


  const leadSourceOpts = agency?.leadSourceOptions ?? []
  // Keep the current value selectable even if an admin has since removed it from the list.
  const leadSourceChoices =
    leadSource && !leadSourceOpts.includes(leadSource) ? [leadSource, ...leadSourceOpts] : leadSourceOpts

  const coreFieldDefs: CoreFieldDef[] = [
    {
      key: 'first_name',
      defaultLabel: 'First name',
      hideable: false,
      render: (label) => (
        <Labeled label={label}>
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={fieldCls} />
        </Labeled>
      ),
    },
    {
      key: 'last_name',
      defaultLabel: 'Last name',
      hideable: true,
      render: (label) => (
        <Labeled label={label}>
          <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={fieldCls} />
        </Labeled>
      ),
    },
    {
      key: 'email',
      defaultLabel: 'Email',
      hideable: false,
      render: (label) => (
        <Labeled label={label}>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={cn(fieldCls, emailError && 'border-danger')}
          />
          {emailError && <p className="text-danger text-[11px] mt-0.5">{emailError}</p>}
        </Labeled>
      ),
    },
    {
      key: 'phone',
      defaultLabel: 'Phone',
      hideable: true,
      render: (label) => (
        <Labeled label={label}>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={cn(fieldCls, phoneError && 'border-danger')}
          />
          {phoneError && <p className="text-danger text-[11px] mt-0.5">{phoneError}</p>}
        </Labeled>
      ),
    },
    {
      key: 'mobile',
      defaultLabel: 'Mobile',
      hideable: true,
      render: (label) => (
        <Labeled label={label}>
          <input
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            className={cn(fieldCls, mobileError && 'border-danger')}
          />
          {mobileError && <p className="text-danger text-[11px] mt-0.5">{mobileError}</p>}
        </Labeled>
      ),
    },
    {
      key: 'owner',
      defaultLabel: 'Owner',
      hideable: true,
      render: (label) => (
        <Labeled label={label}>
          {isAdmin ? (
            <>
              <SearchableSelect
                size="md"
                value={ownerUserId}
                onChange={setOwnerUserId}
                placeholder="Unassigned"
                // Email disambiguates same-named accounts (e.g. two "Rusty" logins).
                options={[
                  { value: '', label: 'Unassigned' },
                  ...(members ?? []).map((u) => ({ value: u.id, label: u.email ? `${u.fullName} (${u.email})` : u.fullName })),
                ]}
                className="w-full"
              />
              {!ownerUserId && (
                <p className="text-[11px] text-amber-600 mt-1">No owner assigned — assign one so it isn't missed.</p>
              )}
              {ownerUserId && (
                <div className="mt-1.5">
                  <OwnerCard member={members?.find((u) => u.id === ownerUserId)} />
                </div>
              )}
            </>
          ) : (
            <>
              {ownerUserId ? (
                <OwnerCard member={members?.find((u) => u.id === ownerUserId)} />
              ) : (
                <p className="px-3 py-2 text-sm text-gray-400 bg-gray-50 border border-gray-100 rounded-lg">Unassigned</p>
              )}
              <p className="text-[11px] text-gray-400 mt-1">Reassigning is done by an admin.</p>
            </>
          )}
        </Labeled>
      ),
    },
    {
      key: 'lead_source',
      defaultLabel: 'Lead source',
      hideable: true,
      render: (label) => (
        <Labeled label={label}>
          <Select
            size="md"
            value={leadSource}
            onChange={(e) => setLeadSource(e.target.value)}
            className="w-full"
          >
            <option value="">—</option>
            {leadSourceChoices.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        </Labeled>
      ),
    },
    {
      // A system field: birthday workflows ("A contact date comes round") read it.
      key: 'birthday',
      defaultLabel: 'Birthday',
      hideable: true,
      render: (label) => (
        <Labeled label={label}>
          <input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} className={fieldCls} />
        </Labeled>
      ),
    },
  ]

  // Consent lives in its own always-visible block (not the hideable layout): a contact must always
  // be re-subscribable, and a hidden opt-out field that Save still writes silently re-applied the
  // opt-out on every edit. Email gets a clear Subscribed/Re-subscribe control; call/SMS stay flags.
  const communicationPrefs = (
    <div className="space-y-3">
      <EmailSubscription
        subscribed={!contact.doNotEmail}
        pending={subscribe.isPending}
        onChange={(subscribed) =>
          subscribe.mutate({
            id: contactId,
            params: {
              firstName: contact.firstName ?? null,
              lastName: contact.lastName ?? null,
              email: contact.email ?? null,
              phone: contact.phone ?? null,
              mobile: contact.mobile ?? null,
              ownerUserId: contact.ownerUserId ?? null,
              leadSource: contact.leadSource ?? null,
              status: contact.status ?? null,
              birthday: contact.birthday,
              nextContactDate: contact.nextContactDate,
              doNotEmail: !subscribed,
              doNotCall: contact.doNotCall,
              doNotSms: contact.doNotSms,
              custom: contact.custom ?? {},
            },
          })
        }
      />
      <PrefToggle label="Calls" allowed={!doNotCall} onLabel="OK to call" offLabel="Do not call" onChange={(ok) => setDoNotCall(!ok)} />
      <PrefToggle label="SMS" allowed={!doNotSms} onLabel="OK to SMS" offLabel="Do not SMS" onChange={(ok) => setDoNotSms(!ok)} />
      <p className="text-[11px] text-gray-400">Call/SMS preferences save with the contact.</p>
    </div>
  )

  const followUp = (
    <div className="space-y-3">
      <Labeled label="Next contact date">
        <input
          type="date"
          value={nextContactDate}
          onChange={(e) => setNextContactDate(e.target.value)}
          className={fieldCls}
        />
      </Labeled>
      <p className="text-[11px] text-gray-400">Saved with the contact.</p>
    </div>
  )

  const coreFields = (
    <div className="space-y-4">
      {resolveCoreLayout(coreFieldDefs, agency?.contactFieldLayout ?? []).map((r) => (
        <div key={r.key}>{r.render(r.label)}</div>
      ))}
    </div>
  )

  const visibleFields = (fields ?? []).filter((f) => !f.hidden)
  const isDate = (t: string) => t === 'date' || t === 'datetime'
  const dateFields = visibleFields.filter((f) => isDate(f.dataType))
  const otherFields = visibleFields.filter((f) => !isDate(f.dataType))
  const customFieldsBlock = visibleFields.length > 0 && (
    <div className="pt-3 border-t border-gray-100 space-y-4">
      {isAdmin && (
        <div className="flex items-center justify-end -mb-2">
          <button
            type="button"
            onClick={() => setShowFields(true)}
            title="Manage fields"
            className="text-gray-400 hover:text-brand-primary"
          >
            <Settings2 size={13} />
          </button>
        </div>
      )}
      {dateFields.length > 0 && (
        <CustomFieldGroup title="Dates" fields={dateFields} custom={custom} setField={setField} />
      )}
      {otherFields.length > 0 && (
        <CustomFieldGroup
          title="Additional details"
          fields={otherFields}
          custom={custom}
          setField={setField}
          collapsibleAfter={6}
        />
      )}
    </div>
  )

  const dealsSection = <ContactDealsSection contactId={contactId} />

  const sendSection = (
    <ContactSendMessage key={contact.id} contact={contact} draft={draft} updateDraft={updateDraft} />
  )

  const timeline = (
    <ActivityLog
      activities={activities}
      add={addActivity}
      update={updateActivity}
      remove={removeActivity}
      pin={pinActivity}
    />
  )

  return (
    <>
      <div className="flex-1 overflow-y-auto bg-gray-50/60">
        {(() => {
          const headerOwnerName = contact.ownerUserId
            ? (members ?? []).find((m) => m.id === contact.ownerUserId)?.fullName ?? null
            : null
          const subscribed = !contact.doNotEmail
          const callNo = (contact.mobile || contact.phone || '').replace(/[^\d+]/g, '')
          const snapshotCard = <ContactSnapshot contact={contact} ownerName={headerOwnerName} activities={activities} />
          const duplicateBanner = isAdmin ? <DuplicateBanner targetId={contact.id} /> : null
          const dealsCard = <Card title="Linked deals">{dealsSection}</Card>
          const tasksCard = <ContactTasksCard contact={contact} adding={addingTask} onAddingChange={setAddingTask} />
          const filesCard = <AttachmentsCard target={{ kind: 'contact', id: contact.id }} />
          const quickMsgCard = (
            <div ref={composerRef}>
              <Card title="Quick message">{sendSection}</Card>
            </div>
          )
          const activityCard = <Card>{timeline}</Card>
          const detailsCard = <Card title="About this contact">{coreFields}</Card>
          const commCard = <Card title="Communication preferences">{communicationPrefs}</Card>
          const followUpCard = <Card title="Follow-up">{followUp}</Card>
          const customCard = visibleFields.length > 0 ? <Card title="Custom fields">{customFieldsBlock}</Card> : null

          return (
            <>
              <div className="border-b border-gray-100 bg-white px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 sticky top-0 z-10">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm min-w-0">
                  <span className="text-gray-500">Owner: <b className="text-brand-dark font-medium">{headerOwnerName ?? 'Unassigned'}</b></span>
                  {contact.leadSource && (
                    <span className="text-gray-500">Source: <span className="text-[11px] font-medium rounded-md px-2 py-0.5 bg-gray-100 text-gray-600">{contact.leadSource}</span></span>
                  )}
                  <span className={cn('text-[11px] font-semibold rounded-full px-2 py-0.5 flex items-center gap-1', subscribed ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500')}>
                    <span className={cn('inline-block w-1.5 h-1.5 rounded-full', subscribed ? 'bg-emerald-500' : 'bg-gray-400')} />
                    {subscribed ? 'Subscribed' : 'Unsubscribed'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 ml-auto">
                  {callNo ? (
                    <a href={`tel:${callNo}`} className={quickBtnCls}><Phone size={13} /> Call</a>
                  ) : (
                    <span className={cn(quickBtnCls, 'opacity-50')}><Phone size={13} /> Call</span>
                  )}
                  {callNo ? (
                    <button type="button" onClick={() => composeIn('sms')} className={quickBtnCls}><MessageSquare size={13} /> SMS</button>
                  ) : (
                    <span className={cn(quickBtnCls, 'opacity-50')}><MessageSquare size={13} /> SMS</span>
                  )}
                  {contact.email ? (
                    <button type="button" onClick={() => composeIn('email')} className={quickBtnCls}><Mail size={13} /> Email</button>
                  ) : (
                    <span className={cn(quickBtnCls, 'opacity-50')}><Mail size={13} /> Email</span>
                  )}
                  <button type="button" onClick={() => setAddingTask(true)} className={quickBtnCls}><CheckSquare size={13} /> Add task</button>
                </div>
              </div>

              <div className="p-4">
                {expanded ? (
                  <div className="space-y-4">
                    {duplicateBanner}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
                      <div className="space-y-4 min-w-0">
                        {detailsCard}
                        {commCard}
                        {customCard}
                      </div>
                      {/* As on a deal: what they ARE on the left, what HAPPENED in the middle (highlights
                          on top), what's CONNECTED / to do on the right. */}
                      <div className="space-y-4 min-w-0">
                        {snapshotCard}
                        {activityCard}
                      </div>
                      <div className="space-y-4 min-w-0">
                        {quickMsgCard}
                        {tasksCard}
                        {dealsCard}
                        {filesCard}
                        {followUpCard}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto max-w-3xl space-y-4">
                    {duplicateBanner}
                    {snapshotCard}
                    {quickMsgCard}
                    {tasksCard}
                    {dealsCard}
                    {filesCard}
                    {activityCard}
                    {detailsCard}
                    {commCard}
                    {customCard}
                    {followUpCard}
                  </div>
                )}
              </div>
            </>
          )
        })()}
      </div>

      <div className="p-4 border-t border-gray-100 flex items-center justify-between bg-white">
        <div className="flex items-center gap-1">
          {/* Delete lives behind "⋯", away from Save — as in HubSpot's Actions menu. */}
          <ActionMenu
            label="More actions for this contact"
            items={[{ label: 'Delete contact', icon: <Trash2 size={15} />, danger: true, onClick: () => setConfirmDelete(true) }]}
          />
          {confirmDelete && (
            <DeleteContactModal contact={contact} onClose={() => setConfirmDelete(false)} onDeleted={onClose} />
          )}
          {isAdmin && <MergeContactControl targetId={contactId} />}
        </div>
        <div className="flex items-center gap-3">
          {!contactMethodsDirty && <SaveState autosave={autosave} onRetry={() => save()} />}
          <button
            onClick={() => save()}
            disabled={update.isPending || hasErrors || !(contactMethodsDirty || detailsDirty)}
            title={contactMethodsDirty ? 'Confirm the phone/email change' : 'Everything is saved'}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {showFields && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFields(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-brand-darkest">Manage contact fields</h2>
              <button
                onClick={() => setShowFields(false)}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <FieldsTab only="contact" />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * "Send message" panel for a single contact — the same composer the deal timeline uses, wired to
 * POST /api/contacts/{id}/send (which logs the send on the contact's timeline and enforces consent).
 */
function ContactSendMessage({
  contact,
  draft,
  updateDraft,
}: {
  contact: Contact
  /** Draft is owned by the card so the header's SMS/Email buttons can preset the channel. */
  draft: ComposerDraft
  updateDraft: (patch: Partial<ComposerDraft>) => void
}) {
  const send = useSendContactMessage(contact.id)
  const [error, setError] = useState<string | null>(null)

  const isSms = draft.channel === 'sms'
  // SMS has no HTML signature, so drop {{agent_signature}} from the token chips (matches the deal composer).
  const tokens = MESSAGE_TOKENS.filter((t) => !isSms || t !== '{{agent_signature}}')
  const sampleContact = { firstName: contact.firstName, lastName: contact.lastName, email: contact.email }

  // Surface the two states the backend would reject up front, so the agent isn't left guessing.
  const optedOut = isSms ? contact.doNotSms : contact.doNotEmail
  const hasRecipient = isSms ? !!(contact.mobile || contact.phone) : !!contact.email
  const hint = optedOut
    ? `This contact has opted out of ${isSms ? 'SMS' : 'email'}.`
    : !hasRecipient
      ? `No ${isSms ? 'mobile number' : 'email address'} on file for this contact.`
      : null

  function doSend({ includeSignature }: { includeSignature: boolean }) {
    if (!draft.templateId && !draft.body.trim()) return
    setError(null)
    send.mutate(
      {
        channel: draft.channel,
        templateId: draft.templateId || undefined,
        subject: draft.templateId || draft.channel !== 'email' ? undefined : draft.subject,
        body: draft.templateId ? undefined : draft.body,
        attachments: draft.channel === 'email' && draft.attachments.length
          ? draft.attachments.map((f) => ({ key: f.key, filename: f.filename, contentType: f.contentType }))
          : undefined,
        includeSignature,
      },
      {
        onSuccess: () => {
          updateDraft({ body: '', subject: '', attachments: [] })
        },
        onError: (e) =>
          setError(
            e instanceof ApiError && e.code === 'consent.blocked'
              ? `This contact has opted out of ${isSms ? 'SMS' : 'email'}.`
              : apiErrorMessage(e),
          ),
      },
    )
  }

  return (
    <div>
      <MessageComposer
        channel={draft.channel}
        onChannelChange={(c) => {
          updateDraft({ channel: c })
          setError(null)
        }}
        templateId={draft.templateId}
        onTemplateChange={(t) => updateDraft({ templateId: t })}
        subject={draft.subject}
        onSubjectChange={(s) => updateDraft({ subject: s })}
        body={draft.body}
        onBodyChange={(b) => updateDraft({ body: b })}
        recipientCount={1}
        onSend={doSend}
        sending={send.isPending}
        sampleContact={sampleContact}
        previewTarget={{ kind: 'contact', id: contact.id }}
        // their latest open deal fills the property / deal fields (server side)
        customFields={['contact', 'deal']}
        tokens={tokens}
        sendLabel="Send"
        inline
        allowAttachments
        attachments={draft.attachments}
        onAttachmentsChange={(a) => updateDraft({ attachments: a })}
      />
      {hint && <p className="text-warn text-xs mt-1">{hint}</p>}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  )
}

/**
 * A contact's deals: the existing links (read-only) plus a "Link to deal" picker. Linking reuses
 * the deal-contact endpoint from the contact side (POST /api/deals/{dealId}/contacts) — the first
 * contact linked to a deal becomes its primary automatically, so no separate step is needed.
 */
function ContactDealsSection({ contactId }: { contactId: string }) {
  const navigate = useNavigate()
  const roleOptions = useDealContactRoles()
  const { data: contactDeals } = useContactDeals(contactId)
  // The deal list is only fetched once the user actually opens the picker.
  const [linking, setLinking] = useState(false)
  const { data: deals, isLoading: dealsLoading } = useDeals({ enabled: linking })
  const link = useLinkDealToContact(contactId)
  const { data: contact } = useContact(contactId)
  const [creatingDeal, setCreatingDeal] = useState(false)

  const [dealId, setDealId] = useState('')
  const [linkRoleRaw, setLinkRole] = useState(
    roleOptions.includes('contact') ? 'contact' : roleOptions[0],
  )
  const [linkError, setLinkError] = useState<string | null>(null)
  // the agency's role list is editable — fall back when the selection no longer exists
  const linkRole = roleOptions.includes(linkRoleRaw) ? linkRoleRaw : roleOptions[0]

  const dealOptions = useMemo(
    () => (deals ?? []).map((d) => ({ value: d.id, label: d.title })),
    [deals],
  )

  function attach() {
    if (!dealId) return
    setLinkError(null)
    link.mutate(
      { dealId, role: linkRole },
      {
        onSuccess: () => setDealId(''),
        onError: (e) =>
          setLinkError(
            e instanceof ApiError && e.code === 'deal.contact_link_exists'
              ? 'This contact already has that role on the deal.'
              : apiErrorMessage(e),
          ),
      },
    )
  }

  return (
    <div>
      {(contactDeals ?? []).length === 0 && <p className="text-xs text-gray-400 mb-2">Not linked to any deals.</p>}
      <div className="space-y-1.5 mb-3">
        {(contactDeals ?? []).map((d) => (
          <div key={`${d.dealId}-${d.role}`} className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => navigate(`/deals/${d.dealId}`)}
              className="flex-1 min-w-0 truncate text-left text-brand-dark hover:text-brand-primary hover:underline"
              title="Open deal"
            >
              {d.title}
            </button>
            <span className="text-[10px] uppercase bg-brand-primary/15 text-brand-primary rounded px-1.5 py-0.5">{d.role}</span>
            <span className="text-[11px] text-gray-400 whitespace-nowrap">{d.stageName}</span>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setCreatingDeal(true)}
        disabled={!contact}
        className="w-full mb-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-brand-primary/40 text-brand-primary rounded-lg text-sm font-medium hover:bg-brand-primary/5 disabled:opacity-60"
      >
        <Plus size={14} /> New deal for {contact ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'this contact' : 'this contact'}
      </button>
      {creatingDeal && contact && (
        <NewDealModal
          contact={contact}
          onClose={() => setCreatingDeal(false)}
          onCreated={(created) => {
            setCreatingDeal(false)
            navigate(`/deals/${created.id}`)
          }}
        />
      )}

      <p className="text-[11px] text-gray-500 mb-1">Or link an existing deal</p>
      <div className="flex gap-2">
        <SearchableSelect
          size="xs"
          value={dealId}
          onChange={setDealId}
          placeholder="Choose a deal…"
          options={dealOptions}
          className="flex-1 min-w-0"
          onOpen={() => setLinking(true)}
          loading={linking && dealsLoading}
        />
        <Select size="xs" value={linkRole} onChange={(e) => setLinkRole(e.target.value)}>
          {roleOptions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
        <button
          onClick={attach}
          disabled={!dealId || link.isPending}
          className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
        >
          Link
        </button>
      </div>
      {linkError && <p className="text-danger text-xs mt-1">{linkError}</p>}
    </div>
  )
}

/** Live email subscription status + one-click flip. Reflects the current server value (so a contact
 *  that (un)subscribed via the email link shows correctly), and the flip saves immediately. */
function EmailSubscription({
  subscribed,
  pending,
  onChange,
}: {
  subscribed: boolean
  pending?: boolean
  onChange: (subscribed: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-sm text-brand-dark">
        <span className={cn('inline-block w-2 h-2 rounded-full', subscribed ? 'bg-success' : 'bg-gray-300')} />
        {subscribed ? 'Subscribed to emails' : 'Unsubscribed from emails'}
      </span>
      <button
        type="button"
        disabled={pending}
        onClick={() => onChange(!subscribed)}
        className={cn(
          'text-xs disabled:opacity-50',
          subscribed
            ? 'text-gray-500 hover:text-danger'
            : 'font-medium text-brand-primary hover:text-brand-dark',
        )}
      >
        {pending ? 'Saving…' : subscribed ? 'Unsubscribe' : 'Re-subscribe'}
      </button>
    </div>
  )
}

const quickBtnCls =
  'flex items-center gap-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50'

/** A green/grey toggle for a communication preference (Calls / SMS), with an allowed/blocked label. */
function PrefToggle({
  label,
  allowed,
  onLabel,
  offLabel,
  onChange,
}: {
  label: string
  allowed: boolean
  onLabel: string
  offLabel: string
  onChange: (allowed: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-sm text-brand-dark">
        <button
          type="button"
          onClick={() => onChange(!allowed)}
          className={cn('relative inline-flex h-5 w-9 items-center rounded-full transition-colors', allowed ? 'bg-emerald-500' : 'bg-gray-300')}
          aria-pressed={allowed}
          aria-label={label}
        >
          <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform', allowed ? 'translate-x-4' : 'translate-x-0.5')} />
        </button>
        {label}
      </span>
      <span className={cn('text-xs font-medium', allowed ? 'text-emerald-600' : 'text-gray-400')}>
        {allowed ? onLabel : offLabel}
      </span>
    </div>
  )
}

/**
 * Admin control to merge a duplicate contact into this one: pick the duplicate (the source), and its
 * deals, activities and list memberships move here, its details fill any blanks, and it's deleted.
 */
/** Admin banner surfacing likely duplicates of this contact, each mergeable into it in one click. */
function DuplicateBanner({ targetId }: { targetId: string }) {
  const { data: dups } = useContactDuplicates(targetId)
  const merge = useMergeContact()
  if (!dups || dups.length === 0) return null
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
      <p className="text-xs font-semibold text-amber-800 mb-2">
        {dups.length} possible duplicate{dups.length === 1 ? '' : 's'} — same email or phone
      </p>
      <div className="space-y-1.5">
        {dups.map((d) => (
          <div key={d.id} className="flex items-center gap-2 text-sm">
            <span className="flex-1 min-w-0 truncate text-brand-dark">
              {contactName(d)}
              <span className="text-gray-400 text-xs ml-1.5">{d.email || d.mobile || d.phone}</span>
            </span>
            <button
              type="button"
              onClick={() => merge.mutate({ targetId, sourceId: d.id })}
              disabled={merge.isPending}
              className="text-xs font-medium text-brand-primary hover:text-brand-dark disabled:opacity-50 shrink-0"
              title="Merge this duplicate into the current contact"
            >
              Merge in
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

function MergeContactControl({ targetId }: { targetId: string }) {
  const [open, setOpen] = useState(false)
  const [sourceId, setSourceId] = useState('')
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [error, setError] = useState<string | null>(null)
  const merge = useMergeContact()

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300)
    return () => clearTimeout(t)
  }, [query])
  const { data: firstPage } = useContacts()
  const { data: searchPage } = useContactSearch(debounced)
  const page = debounced ? searchPage : firstPage
  const candidates = (page?.items ?? []).filter((c) => c.id !== targetId)
  const nameOf = (c: { firstName: string | null; lastName: string | null; email: string | null }) =>
    [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Unnamed contact'

  function close() {
    setOpen(false)
    setSourceId('')
    setQuery('')
    setError(null)
  }

  function doMerge() {
    if (!sourceId) return
    setError(null)
    merge.mutate({ targetId, sourceId }, { onSuccess: close, onError: (e) => setError(apiErrorMessage(e)) })
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="px-3 py-2 text-sm text-gray-500 hover:text-brand-dark"
        title="Merge a duplicate contact into this one"
      >
        Merge…
      </button>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={close} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="merge-title"
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 id="merge-title" className="text-base font-semibold text-brand-darkest">
                Merge a duplicate into this contact
              </h2>
              <button onClick={close} className="text-gray-400 hover:text-gray-600" title="Close">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3 flex-1 min-h-0 flex flex-col">
              <p className="text-sm text-gray-500">
                Pick the duplicate to merge in. Its deals, activities and list memberships move to this contact,
                its details fill any blanks here, and the duplicate is deleted. This can't be undone.
              </p>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email or phone…"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
              />
              <ul role="listbox" aria-label="Contacts" className="flex-1 min-h-48 max-h-96 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
                {candidates.length === 0 && (
                  <li className="px-3 py-6 text-center text-sm text-gray-400">No contacts match.</li>
                )}
                {candidates.map((c) => {
                  const on = c.id === sourceId
                  return (
                    <li key={c.id} role="option" aria-selected={on}>
                      <button
                        type="button"
                        onClick={() => setSourceId(c.id)}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-3 ${
                          on ? 'bg-brand-primary/10' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full border shrink-0 ${
                            on ? 'border-brand-primary bg-brand-primary' : 'border-gray-300'
                          }`}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-brand-darkest truncate">{nameOf(c)}</span>
                          <span className="block text-xs text-gray-500 truncate">
                            {[c.email, c.mobile || c.phone].filter(Boolean).join(' · ') || 'No email or phone'}
                          </span>
                        </span>
                        <span className="text-[11px] text-gray-400 shrink-0">
                          Added {formatShortDate(c.createdAt)}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
              {error && <p className="text-danger text-xs">{error}</p>}
            </div>
            <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
              <button onClick={close} className="px-3 py-2 text-sm text-gray-500 hover:text-brand-dark">
                Cancel
              </button>
              <button
                onClick={doMerge}
                disabled={!sourceId || merge.isPending}
                className="px-4 py-2 bg-danger text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-60"
              >
                {merge.isPending ? 'Merging…' : 'Merge & delete duplicate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  )
}

function SnapshotTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">{label}</div>
      <div className="text-sm font-medium text-brand-dark truncate">{value}</div>
    </div>
  )
}

/** At-a-glance summary of the contact, mirroring the deal snapshot: owner, source, subscription,
 *  last activity, created, and how many deals they're linked to. */
function ContactSnapshot({
  contact,
  ownerName,
  activities,
}: {
  contact: Contact
  ownerName: string | null
  activities: Activity[] | undefined
}) {
  const { data: deals } = useContactDeals(contact.id)
  const lastActivity = (activities ?? [])
    .slice()
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
  const last = lastActivity
    ? relativeDay(lastActivity.createdAt)
    : contact.lastContactedAt
      ? relativeDay(contact.lastContactedAt)
      : 'None yet'

  return (
    <Card title="Snapshot">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
        <SnapshotTile label="Owner" value={ownerName ?? 'Unassigned'} />
        <SnapshotTile label="Source" value={contact.leadSource || '—'} />
        <SnapshotTile
          label="Email"
          value={
            <span className={cn('text-xs font-semibold rounded-full px-2 py-0.5', contact.doNotEmail ? 'bg-gray-100 text-gray-500' : 'bg-emerald-50 text-emerald-600')}>
              {contact.doNotEmail ? 'Unsubscribed' : 'Subscribed'}
            </span>
          }
        />
        <SnapshotTile label="Last activity" value={last} />
        <SnapshotTile label="Created" value={formatShortDate(contact.createdAt)} />
        <SnapshotTile label="Linked deals" value={String((deals ?? []).length)} />
        <SnapshotTile
          label="Lifecycle"
          value={
            <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-brand-primary/10 text-brand-primary capitalize">
              {contact.status || 'active'}
            </span>
          }
        />
      </div>
    </Card>
  )
}
